import React, { useState, useEffect } from "react";
import { Building2, Layers, Workflow, Tag, Save, Plus, Check, Upload, ShieldAlert, Trash2, GitMerge, RotateCcw, Sparkles, Mail, Send, Eye, Users, UserPlus, Hash, Calculator, FlaskConical, Eraser } from "lucide-react";
import { PageHead, Loader } from "../components/PageHead";
import { Modal } from "../components/Modal";
import { DataTable } from "../components/DataTable";
import { money, fmtDate } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import api, { apiErr } from "../lib/api";
import { toast } from "sonner";
import DataImport from "./DataImport";
import DedupeTab from "./DedupeTab";
import RecallTab from "./RecallTab";

const TABS = [
  ["company", "Company", Building2],
  ["disciplines", "Disciplines", Layers],
  ["subcontractors", "Subcontractors", Workflow],
  ["pricelist", "Price List", Tag],
  ["numbering", "Numbering", Hash],
  ["import", "Data Import", Upload],
  ["dedupe", "Dedupe", GitMerge],
  ["recall", "Emails", Mail],
  ["envdemo", "Test / Demo Env", FlaskConical],
  ["danger", "Danger Zone", ShieldAlert],
];

export default function Settings() {
  const { user, can } = useAuth();
  const canEdit = can("settings", "edit");
  const canImport = can("import", "view");
  const isManager = user?.role === "manager";
  const [tab, setTab] = useState("company");

  const visibleTabs = TABS.filter(([k]) => {
    if (k === "import") return canImport;
    if (k === "dedupe") return isManager || user?.role === "admin";
    if (k === "recall") return isManager || user?.role === "admin";
    if (k === "envdemo") return isManager || user?.role === "admin";
    if (k === "danger") return isManager;
    return true;
  });

  return (
    <>
      <PageHead crumb="Administration" title="Settings" sub="Company profile, reference data, and data import." />
      <div className="ftabs">
        {visibleTabs.map(([k, label, Icon]) => (
          <button key={k} className={`ftab ${tab === k ? "active" : ""}`} onClick={() => setTab(k)} data-testid={`settings-tab-${k}`}>
            <Icon size={13} style={{ verticalAlign: "-2px", marginRight: 5 }} />{label}
          </button>
        ))}
      </div>

      {tab === "company" && <CompanyTab canEdit={canEdit} />}
      {tab === "disciplines" && <DisciplinesTab canEdit={canEdit} />}
      {tab === "subcontractors" && <SubconTab canEdit={canEdit} />}
      {tab === "pricelist" && <PriceTab canEdit={canEdit} />}
      {tab === "numbering" && <NumberingTab canEdit={canEdit} />}
      {tab === "import" && canImport && <DataImport embedded />}
      {tab === "dedupe" && (isManager || user?.role === "admin") && <DedupeTab />}
      {tab === "recall" && (isManager || user?.role === "admin") && <RecallTab canSend={isManager} />}
      {tab === "envdemo" && (isManager || user?.role === "admin") && <TestEnvTab />}
      {tab === "danger" && isManager && <DangerZoneTab />}
    </>
  );
}

function CompanyTab({ canEdit }) {
  const [v, setV] = useState(null);
  useEffect(() => { api.get("/settings/company").then((r) => setV(r.data.value || {})).catch((e) => toast.error(apiErr(e))); }, []);
  if (!v) return <Loader />;
  const save = async () => { try { await api.put("/settings/company", { value: v }); toast.success("Company profile saved"); } catch (e) { toast.error(apiErr(e)); } };
  const F = ([k, l]) => <div className="fld" key={k}><label>{l}</label><input className="input" value={v[k] || ""} disabled={!canEdit} onChange={(e) => setV({ ...v, [k]: e.target.value })} data-testid={`company-${k}`} /></div>;
  return (
    <div className="card pad" style={{ maxWidth: 760 }}>
      <div className="form-grid">
        {[["name", "Company name"], ["trading_name", "Trading name"], ["email", "Email"], ["phone", "Phone"], ["vat_number", "VAT number"], ["registration", "Registration no."], ["sanas_facility", "SANAS facility no."], ["tagline", "Tagline"]].map(F)}
      </div>
      <div className="fld" style={{ marginTop: 14 }}><label>Address</label><textarea className="input" rows={2} value={v.address || ""} disabled={!canEdit} onChange={(e) => setV({ ...v, address: e.target.value })} /></div>
      {canEdit && <div style={{ marginTop: 16 }}><button className="btn gold" onClick={save} data-testid="company-save"><Save size={15} /> Save profile</button></div>}
    </div>
  );
}

function DisciplinesTab({ canEdit }) {
  const [refresh, setRefresh] = useState(0);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const add = async () => { if (!name) return; try { await api.post("/disciplines", { name }); toast.success("Discipline added"); setAdding(false); setName(""); setRefresh((x) => x + 1); } catch (e) { toast.error(apiErr(e)); } };
  const columns = [
    { key: "name", label: "Discipline", sortable: true, editable: true, render: (v) => <span className="t-strong">{v}</span> },
    { key: "active", label: "Active", render: (v) => v === false ? <span className="badge b-red"><span className="bd" />Inactive</span> : <span className="badge b-green"><span className="bd" />Active</span> },
  ];
  return (
    <>
      {canEdit && <div style={{ marginBottom: 12 }}><button className="btn gold sm" onClick={() => setAdding(true)} data-testid="add-discipline"><Plus size={14} /> Add discipline</button></div>}
      <DataTable endpoint="/disciplines" columns={columns} rowKey="id" testid="disciplines" clientSide
        canEdit={canEdit} editEndpoint={(row) => `/disciplines/${row.id}`} defaultSort={{ by: "name", dir: "asc" }}
        searchPlaceholder="Search disciplines…" refreshSignal={refresh} />
      {adding && (
        <Modal open onClose={() => setAdding(false)} icon={Plus} title="Add discipline"
          footer={<><button className="btn ghost" onClick={() => setAdding(false)}>Cancel</button><button className="btn gold" onClick={add}>Add</button></>}>
          <div className="fld"><label>Name</label><input className="input" value={name} onChange={(e) => setName(e.target.value)} data-testid="discipline-name" /></div>
        </Modal>
      )}
    </>
  );
}

function DuplicateNamesCard({ canEdit }) {
  const [scan, setScan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [merging, setMerging] = useState(false);

  const runScan = async () => {
    setLoading(true);
    try {
      const r = await api.get("/subcontractors/duplicate-names");
      setScan(r.data);
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };
  useEffect(() => { runScan(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const mergeSafe = async () => {
    if (!scan?.safe_count) return;
    setMerging(true);
    try {
      const r = await api.post("/subcontractors/merge-duplicates", { dry_run: false });
      toast.success(`Merged ${r.data.merged_count} duplicate name group${r.data.merged_count === 1 ? "" : "s"}`);
      await runScan();
    } catch (e) { toast.error(apiErr(e)); } finally { setMerging(false); }
  };

  if (loading && !scan) return null;
  if (scan && scan.total_duplicate_names === 0) return null;

  return (
    <div className="card pad" style={{ maxWidth: 640, marginBottom: 16, background: "#fff4f1", border: "1px solid #e3a99a" }} data-testid="subcon-duplicates-card">
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
        <div style={{ width: 34, height: 34, background: "#fbe0da", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", color: "#a3401f" }}>
          <GitMerge size={17} />
        </div>
        <div style={{ flex: 1 }}>
          <div className="t-strong" style={{ fontSize: 13.5 }} data-testid="subcon-duplicates-count">
            {scan?.total_duplicate_names} duplicate subcontractor name{scan?.total_duplicate_names === 1 ? "" : "s"} found
          </div>
          <div className="t-mut" style={{ fontSize: 12 }}>
            Legacy imports registered some subcontractors twice (one active, one inactive) —
            this made older name-only jobs ambiguous about which address to use.
          </div>
        </div>
        {canEdit && scan?.safe_count > 0 && (
          <button className="btn gold sm" onClick={mergeSafe} disabled={merging} data-testid="subcon-duplicates-merge-safe">
            <GitMerge size={13} /> {merging ? "Merging…" : `Merge ${scan.safe_count} safe`}
          </button>
        )}
      </div>
      {(scan?.groups || []).map((g) => (
        <div key={g.name} className="card pad" style={{ marginTop: 8, background: "var(--card)" }}
          data-testid={`subcon-duplicate-group-${g.name}`}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span className="t-strong" style={{ fontSize: 13 }}>{g.name}</span>
            {g.safe_to_merge
              ? <span className="badge b-green"><span className="bd" />Safe to auto-merge</span>
              : <span className="badge b-amber"><span className="bd" />{g.reason}</span>}
          </div>
          <table className="dt" style={{ marginTop: 6 }}>
            <thead><tr><th>Vendor #</th><th>Active</th><th>Address</th><th>Linked items</th><th>Linked equipment</th></tr></thead>
            <tbody>
              {g.members.map((m) => (
                <tr key={m.id}>
                  <td className="mono">{m.vendor_number || "—"}</td>
                  <td>{m.active
                    ? <span className="badge b-green"><span className="bd" />Active</span>
                    : <span className="badge b-red"><span className="bd" />Inactive</span>}</td>
                  <td style={{ fontSize: 12 }}>{m.address || "—"}</td>
                  <td className="mono">{m.linked_job_items}</td>
                  <td className="mono">{m.linked_equipment}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

function MessyAddressesCard({ canEdit }) {
  const [scan, setScan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const runScan = async () => {
    setLoading(true);
    try {
      const r = await api.get("/subcontractors/messy-addresses");
      setScan(r.data);
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };
  useEffect(() => { runScan(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const cleanAll = async () => {
    if (!scan?.total) return;
    setCleaning(true);
    try {
      const r = await api.post("/subcontractors/clean-addresses", { dry_run: false });
      toast.success(`Cleaned ${r.data.updated_count} address${r.data.updated_count === 1 ? "" : "es"}`);
      await runScan();
    } catch (e) { toast.error(apiErr(e)); } finally { setCleaning(false); }
  };

  if (loading && !scan) return null;
  if (scan && scan.total === 0) return null;

  return (
    <div className="card pad" style={{ maxWidth: 640, marginBottom: 16, background: "#fff4f1", border: "1px solid #e3a99a" }} data-testid="subcon-messy-addresses-card">
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: expanded ? 8 : 0 }}>
        <div style={{ width: 34, height: 34, background: "#fbe0da", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", color: "#a3401f" }}>
          <Eraser size={17} />
        </div>
        <div style={{ flex: 1, cursor: "pointer" }} onClick={() => setExpanded((x) => !x)}>
          <div className="t-strong" style={{ fontSize: 13.5 }} data-testid="subcon-messy-addresses-count">
            {scan?.total} subcontractor address{scan?.total === 1 ? "" : "es"} need cleanup
          </div>
          <div className="t-mut" style={{ fontSize: 12 }}>
            Legacy imports left contact names (e.g. "ATT: Nadine") or a duplicated city baked
            straight into the Address field — this printed a garbled address on Delivery
            Notes / Subcontractor DNs. {expanded ? "Click to collapse." : "Click to review."}
          </div>
        </div>
        {canEdit && scan?.total > 0 && (
          <button className="btn gold sm" onClick={cleanAll} disabled={cleaning} data-testid="subcon-messy-addresses-clean-all">
            <Eraser size={13} /> {cleaning ? "Cleaning…" : `Clean ${scan.total}`}
          </button>
        )}
      </div>
      {expanded && (
        <table className="dt" style={{ marginTop: 8 }}>
          <thead><tr><th>Vendor #</th><th>Name</th><th>Current address</th><th>Proposed address</th></tr></thead>
          <tbody>
            {(scan?.rows || []).map((r) => (
              <tr key={r.id} data-testid={`subcon-messy-address-row-${r.id}`}>
                <td className="mono">{r.vendor_number || "—"}</td>
                <td>{r.name}</td>
                <td style={{ fontSize: 12, color: "#a3401f" }}>{r.current_address}</td>
                <td style={{ fontSize: 12 }}>{r.proposed_address || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function SubconTab({ canEdit }) {
  const { can } = useAuth();
  const canCostEdit = can("subcontractor_costing", "edit");
  const canCostView = can("subcontractor_costing", "view");
  const [refresh, setRefresh] = useState(0);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: "", contact_person: "", email: "", contact_number: "", city: "" });
  const add = async () => { if (!form.name) return toast.error("Name required"); try { await api.post("/subcontractors", form); toast.success("Subcontractor added"); setAdding(false); setForm({ name: "", contact_person: "", email: "", contact_number: "", city: "" }); setRefresh((x) => x + 1); } catch (e) { toast.error(apiErr(e)); } };
  const columns = [
    { key: "name", label: "Name", sortable: true, editable: true, render: (v) => <span className="t-strong">{v}</span> },
    { key: "contact_person", label: "Contact", sortable: true, editable: true },
    { key: "email", label: "Email", sortable: true, editable: true, mono: true },
    { key: "contact_number", label: "Phone", editable: true, mono: true },
    { key: "city", label: "City", sortable: true, editable: true },
  ];
  return (
    <>
      {canCostView && <SubconMarkupCard canEdit={canCostEdit} />}
      <DuplicateNamesCard canEdit={canEdit} />
      <MessyAddressesCard canEdit={canEdit} />
      {canEdit && <div style={{ marginBottom: 12 }}><button className="btn gold sm" onClick={() => setAdding(true)} data-testid="add-subcon"><Plus size={14} /> Add subcontractor</button></div>}
      <DataTable endpoint="/subcontractors" params={{ limit: 500 }} columns={columns} rowKey="id" testid="subcontractors" clientSide
        canEdit={canEdit} editEndpoint={(row) => `/subcontractors/${row.id}`} defaultSort={{ by: "name", dir: "asc" }}
        searchPlaceholder="Search subcontractors…" refreshSignal={refresh} />
      {adding && (
        <Modal open onClose={() => setAdding(false)} icon={Plus} title="Add subcontractor"
          footer={<><button className="btn ghost" onClick={() => setAdding(false)}>Cancel</button><button className="btn gold" onClick={add}>Add</button></>}>
          <div className="form-grid">
            <div className="fld"><label>Name <span className="req">*</span></label><input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="subcon-name" /></div>
            <div className="fld"><label>Contact person</label><input className="input" value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} /></div>
            <div className="fld"><label>Email</label><input className="input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div className="fld"><label>Phone</label><input className="input" value={form.contact_number} onChange={(e) => setForm({ ...form, contact_number: e.target.value })} /></div>
          </div>
        </Modal>
      )}
    </>
  );
}

function SubconMarkupCard({ canEdit }) {
  const [pct, setPct] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/subcontractor-costing/config")
      .then((r) => { setPct(String(r.data.default_markup_pct ?? 45)); setLoaded(true); })
      .catch((e) => { toast.error(apiErr(e)); setLoaded(true); });
  }, []);
  const save = async () => {
    const v = Number(pct);
    if (Number.isNaN(v) || v < 0) return toast.error("Enter a valid markup percentage");
    setBusy(true);
    try {
      const r = await api.put("/subcontractor-costing/config", { default_markup_pct: v });
      toast.success(`Default markup set to ${r.data.default_markup_pct}%`);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="card pad" style={{ maxWidth: 560, marginBottom: 16, background: "#fffaf0", border: "1px solid #e3d28a" }} data-testid="subcon-markup-card">
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
        <div style={{ width: 34, height: 34, background: "#fff1c1", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", color: "#8a5a00" }}>
          <Calculator size={17} />
        </div>
        <div>
          <div className="t-strong" style={{ fontSize: 13.5 }}>Subcontractor quote markup (internal)</div>
          <div className="t-mut" style={{ fontSize: 12 }}>Default % added on top of subcontractor cost to suggest a PTE quote. Not accounting — Sage stays the system of record.</div>
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 10 }}>
        <div className="fld" style={{ marginBottom: 0, width: 160 }}>
          <label>Default markup %</label>
          <input className="input mono" type="number" step="0.5" value={pct} disabled={!canEdit || busy || !loaded}
            onChange={(e) => setPct(e.target.value)} data-testid="subcon-markup-input" />
        </div>
        {canEdit && (
          <button className="btn gold" onClick={save} disabled={busy || !loaded} data-testid="subcon-markup-save">
            <Save size={14} /> {busy ? "Saving…" : "Save"}
          </button>
        )}
      </div>
    </div>
  );
}

function PriceTab({ canEdit }) {
  const columns = [
    { key: "description", label: "Description", sortable: true, editable: true, render: (v) => <span className="t-strong">{v || "—"}</span> },
    { key: "part_number", label: "Part no.", sortable: true, editable: true, mono: true },
    { key: "pastel_code", label: "Pastel code", editable: true, mono: true },
    { key: "price_excl", label: "Price excl.", sortable: true, editable: true, type: "number", align: "right", render: (v) => money(v) },
    { key: "price_incl", label: "Price incl.", sortable: true, editable: true, type: "number", align: "right", render: (v) => money(v) },
  ];
  return (
    <DataTable endpoint="/price-list" columns={columns} rowKey="id" testid="price-list"
      canEdit={canEdit} editEndpoint={(row) => `/price-list/${row.id}`} defaultSort={{ by: "description", dir: "asc" }}
      searchPlaceholder="Search price list…" />
  );
}


function NumberingTab({ canEdit }) {
  const [info, setInfo] = useState(null);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/cert-number/current")
    .then((r) => { setInfo(r.data); setInput(r.data.last_certificate_number || ""); })
    .catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, []);
  if (!info) return <Loader />;

  const save = async () => {
    const v = (input || "").trim();
    if (!v) return toast.error("Enter the last issued certificate number, e.g. CR26-12345");
    setBusy(true);
    try {
      const r = await api.put("/cert-number/seed", { last_certificate_number: v });
      toast.success(`Saved. Next certificate will be ${r.data.next_certificate_number}`, { duration: 7000 });
      await load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="card pad" style={{ maxWidth: 680 }} data-testid="numbering-card">
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
        <div style={{ width: 36, height: 36, background: "#fff1c1", borderRadius: 9, display: "flex", alignItems: "center", justifyContent: "center", color: "#8a5a00" }}>
          <Hash size={18} />
        </div>
        <div>
          <div className="t-strong" style={{ fontSize: 14 }}>Certificate numbering</div>
          <div className="t-mut" style={{ fontSize: 12.5 }}>
            CR numbers run as <span className="mono">CR{info.year}-NNNNN</span>, sequential per year.
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, margin: "14px 0" }}>
        <div className="card pad" style={{ background: "var(--card-2)", padding: "12px 14px" }}>
          <div className="t-mut" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".05em" }}>Last issued</div>
          <div className="t-strong mono" style={{ fontSize: 20 }} data-testid="numbering-last">{info.last_certificate_number || "—"}</div>
        </div>
        <div className="card pad" style={{ background: "#fffaf0", border: "1px solid #e3d28a", padding: "12px 14px" }}>
          <div className="t-mut" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".05em" }}>Next will be</div>
          <div className="t-strong mono" style={{ fontSize: 20, color: "#8a5a00" }} data-testid="numbering-next">{info.next_certificate_number}</div>
        </div>
      </div>

      {info.floor_active && (
        <div className="t-mut" style={{ fontSize: 12, marginBottom: 12 }}>
          Go-live seed set{info.seed_set_by ? ` by ${info.seed_set_by}` : ""}{info.seed_set_at ? ` on ${fmtDate(info.seed_set_at)}` : ""}.
        </div>
      )}

      <div className="card pad" style={{ background: "#f7f7f4", border: "1px solid var(--line)", fontSize: 12.5, lineHeight: 1.55, marginBottom: 14 }}>
        <strong>Going live?</strong> Enter the <em>last</em> CR number you issued in the old system (Caspio).
        The next certificate created here will continue from the following number. You can only set a number
        at or above what's already been issued in this system ({info.last_certificate_number || `CR${info.year}-00000`}).
      </div>

      <div className="fld">
        <label>Last issued certificate number</label>
        <input className="input mono" value={input} disabled={!canEdit || busy}
          onChange={(e) => setInput(e.target.value)} placeholder={`CR${info.year}-12345`}
          autoComplete="off" spellCheck={false} data-testid="numbering-input" />
      </div>
      {canEdit && (
        <div style={{ marginTop: 14 }}>
          <button className="btn gold" onClick={save} disabled={busy} data-testid="numbering-save">
            <Save size={15} /> {busy ? "Saving…" : "Set starting number"}
          </button>
        </div>
      )}
    </div>
  );
}


function TestEnvTab() {
  const { env, switchEnv, toggleEnabled } = useAuth();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [toggleBusy, setToggleBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [result, setResult] = useState(null);

  const load = () => api.get("/env/status").then((r) => setStatus(r.data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, []);

  const doReset = async () => {
    setBusy(true);
    try {
      const r = await api.post("/env/reset-demo");
      setResult(r.data);
      toast.success(`Demo data refreshed — ${r.data.collections_cloned} collection(s) cloned from Live`);
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const doSetToggle = async (enabled) => {
    if (enabled === false && !window.confirm(
      "Disable Live/Test switching?\n\n" +
      "Every user (any role) will work directly in Live data — the Test/Demo sandbox becomes " +
      "unreachable via the UI for everyone until you turn this back on. Use this once you're " +
      "confidently live and don't want the sandbox distinction getting in the way operationally."
    )) return;
    setToggleBusy(true);
    try {
      await api.put("/env/toggle-setting", { enabled });
      toast.success(enabled ? "Live/Test switching re-enabled." : "Live/Test switching disabled — everyone is on Live now.");
      load();
      window.location.reload();
    } catch (e) { toast.error(apiErr(e)); } finally { setToggleBusy(false); }
  };

  const closeConfirm = () => { setConfirmOpen(false); setResult(null); };

  return (
    <div style={{ display: "grid", gap: 14, maxWidth: 720 }}>
      <div className="card pad" style={{ border: "1px solid #c79c2d" }} data-testid="live-test-toggle-card">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 260 }}>
            <div className="t-strong" style={{ fontSize: 14 }}>Live / Test switching</div>
            <div className="t-mut" style={{ fontSize: 12.5, marginTop: 2 }}>
              {toggleEnabled
                ? "Currently ON — admin/manager can switch between Live and the sandboxed Test/Demo environment."
                : "Currently OFF — everyone (any role) works directly in Live. The Test/Demo sandbox is not reachable via the UI."}
            </div>
          </div>
          <button className="btn sm"
            style={{ background: toggleEnabled ? "#a32424" : "#36652c", color: "#fff", borderColor: toggleEnabled ? "#a32424" : "#36652c" }}
            onClick={() => doSetToggle(!toggleEnabled)} disabled={toggleBusy}
            data-testid="live-test-toggle-btn">
            {toggleBusy ? "Saving…" : toggleEnabled ? "Disable — go straight to Live" : "Re-enable Live/Test switching"}
          </button>
        </div>
      </div>

      {toggleEnabled && (
      <div className="card pad" style={{ background: "#fffaf0", border: "1px solid #e3d28a" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
          <FlaskConical size={18} style={{ color: "#8a5a00" }} />
          <span className="t-strong" style={{ fontSize: 14 }}>Live / Test (Demo) environment</span>
        </div>
        <p className="t-mut" style={{ fontSize: 13, lineHeight: 1.55, margin: 0 }}>
          Everyone except admin/manager always works in <strong>Test / Demo</strong> mode — a full
          sandboxed clone of the real data, safe to explore and break. Outgoing emails are
          hard-blocked while in Test mode. Admin/manager can switch to <strong>Live</strong> anytime
          using the toggle in the top-right of every page.
        </p>
      </div>
      )}

      {toggleEnabled && (
      <div className="card pad">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div>
            <div className="t-strong" style={{ fontSize: 13.5 }}>You're currently viewing</div>
            <div className="t-mut" style={{ fontSize: 12.5 }}>Switching reloads the page against the new environment.</div>
          </div>
          <div className="env-switch" style={{ background: "var(--ink)" }}>
            <button className={`env-seg ${env === "live" ? "active-live" : ""}`} onClick={() => switchEnv("live")} data-testid="settings-env-live">Live</button>
            <button className={`env-seg ${env === "test" ? "active-test" : ""}`} onClick={() => switchEnv("test")} data-testid="settings-env-test">Test</button>
          </div>
        </div>
      </div>
      )}

      <div className="card pad" style={{ border: "1px solid #c79c2d", opacity: toggleEnabled ? 1 : 0.5 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 36, height: 36, background: "#fff1c1", borderRadius: 9, display: "flex", alignItems: "center", justifyContent: "center", color: "#8a5a00" }}>
            <RotateCcw size={18} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="t-strong" style={{ fontSize: 14 }}>Reset Demo Data (re-clone from Live)</div>
            <div className="s t-mut" style={{ fontSize: 12.5, marginTop: 2 }}>
              Wipes the Test database and copies every Live collection fresh — takes a few seconds.
              Use this whenever the demo data gets messy from people playing around.
            </div>
            {status?.last_demo_reset_at && (
              <div className="t-mut" style={{ fontSize: 12, marginTop: 6 }}>
                Last reset {fmtDate(status.last_demo_reset_at)}{status.last_demo_reset_by ? ` by ${status.last_demo_reset_by}` : ""}
              </div>
            )}
          </div>
          <button className="btn sm" style={{ background: "#c79c2d", color: "#fff", borderColor: "#c79c2d" }}
            onClick={() => setConfirmOpen(true)} disabled={!toggleEnabled} data-testid="reset-demo-btn">
            <RotateCcw size={13} /> Reset demo data…
          </button>
        </div>
      </div>

      {confirmOpen && (
        <Modal open onClose={busy ? () => {} : closeConfirm} icon={RotateCcw}
          title="Reset Demo Data" subtitle="Clones every Live collection into the Test database."
          footer={<>
            <button className="btn ghost" onClick={closeConfirm} disabled={busy}>{result ? "Close" : "Cancel"}</button>
            {!result && (
              <button className="btn sm" style={{ background: "#c79c2d", color: "#fff", borderColor: "#c79c2d" }}
                onClick={doReset} disabled={busy} data-testid="reset-demo-confirm">
                <RotateCcw size={13} /> {busy ? "Cloning…" : "Yes, reset demo data"}
              </button>
            )}
          </>}>
          {!result ? (
            <p style={{ fontSize: 13.5, lineHeight: 1.55 }}>
              This overwrites everything currently in the Test/Demo environment with a fresh copy of
              Live. Anything anyone has changed while testing will be lost. This does <strong>not</strong>
              touch Live data at all.
            </p>
          ) : (
            <div className="card pad" style={{ background: "#dff6e3", border: "1px solid #b5e0c0" }}>
              {result.collections_cloned} collection(s) cloned successfully.
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}


function DangerZoneTab() {
  return (
    <div style={{ display: "grid", gap: 14 }}>
      <div className="card pad" style={{ background: "#fff4d6", border: "1px solid #e3d28a" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
          <ShieldAlert size={18} style={{ color: "#8a5a00" }} />
          <span className="t-strong" style={{ fontSize: 14 }}>Manager-only destructive actions</span>
        </div>
        <p className="t-mut" style={{ fontSize: 13, lineHeight: 1.55, margin: 0 }}>
          Use these when wiping data before a fresh Caspio re-import, or when removing a customer's data on request.
          All deletions <strong>cascade</strong> to linked calibration events, certificates and open job-line items.
          Closed / dispatched / paid job-lines are preserved for history. Every action is logged to the audit trail.
        </p>
      </div>
      <DeleteAllCard scope="all" />
      <DeleteAllCard scope="customer" />
      <CustomerCsvImportCard />
      <ResetReseedCard />
    </div>
  );
}

function DeleteAllCard({ scope }) {
  const [open, setOpen] = useState(false);
  const [confirmation, setConfirmation] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [customers, setCustomers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (scope !== "customer") return;
    api.get("/customers", { params: { limit: 500, sort_by: "customer_name", sort_dir: "asc" } })
      .then((r) => setCustomers(r.data.items || []))
      .catch(() => {});
  }, [scope]);

  const isGlobal = scope === "all";
  const title = isGlobal ? "Wipe ALL equipment globally" : "Wipe equipment for a single customer";
  const desc = isGlobal
    ? "Removes every equipment record across every customer, plus their calibration events, certificates and open job-lines. Typically used before a fresh full Caspio re-import."
    : "Removes every equipment record for one customer, plus their calibration events, certificates and open job-lines. Other customers are untouched.";

  const submit = async () => {
    if (!isGlobal && !customerId) return toast.error("Please pick a customer first");
    setBusy(true);
    try {
      const body = { confirmation };
      if (!isGlobal) body.customer_id = customerId;
      const r = await api.post("/equipment/delete-all", body);
      setResult(r.data);
      toast.success(`${r.data.equipment.toLocaleString()} equipment record(s) purged from ${r.data.scope}`);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const closeAll = () => { setOpen(false); setConfirmation(""); setResult(null); setCustomerId(""); };

  return (
    <>
      <div className="card pad" style={{ border: "1px solid #e3a4a4", background: "#fff" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 36, height: 36, background: "#fde2e2", borderRadius: 9, display: "flex", alignItems: "center", justifyContent: "center", color: "#a32424" }}>
            <Trash2 size={18} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="t-strong" style={{ fontSize: 14 }}>{title}</div>
            <div className="s t-mut" style={{ fontSize: 12.5, marginTop: 2 }}>{desc}</div>
          </div>
          <button className="btn sm"
            style={{ background: "#a32424", color: "#fff", borderColor: "#a32424" }}
            onClick={() => setOpen(true)}
            data-testid={isGlobal ? "danger-delete-all-global" : "danger-delete-all-customer"}>
            <Trash2 size={13} /> {isGlobal ? "Delete all equipment…" : "Delete customer's equipment…"}
          </button>
        </div>
      </div>

      {open && (
        <Modal open onClose={busy ? () => {} : closeAll} icon={ShieldAlert} title={title}
          subtitle="This action cannot be undone. Read carefully."
          footer={<>
            <button className="btn ghost" onClick={closeAll} disabled={busy}>Cancel</button>
            {!result && (
              <button
                className="btn sm"
                style={{ background: "#a32424", color: "#fff", borderColor: "#a32424" }}
                onClick={submit}
                disabled={busy || confirmation.trim() !== "DELETE ALL EQUIPMENT" || (!isGlobal && !customerId)}
                data-testid="danger-confirm-submit">
                <Trash2 size={13} /> {busy ? "Wiping…" : "I understand — wipe data"}
              </button>
            )}
          </>}>
          {!result ? (
            <>
              <div className="card pad" style={{ background: "#fde2e2", border: "1px solid #e3a4a4", color: "#a32424", marginBottom: 14, fontSize: 13 }}>
                <strong>Warning:</strong> this will permanently delete all equipment{isGlobal ? "" : " for the selected customer"}, along with linked calibration events, certificates and OPEN job-line items. Already-dispatched / closed / paid job-lines are preserved for history.
              </div>

              {!isGlobal && (
                <div className="fld" style={{ marginBottom: 14 }}>
                  <label>Customer <span className="req">*</span></label>
                  <select className="input" value={customerId} onChange={(e) => setCustomerId(e.target.value)} data-testid="danger-customer-select">
                    <option value="">— pick a customer —</option>
                    {customers.map((c) => (
                      <option key={c.id} value={c.id}>{c.customer_number ? `${c.customer_number} · ` : ""}{c.customer_name}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="fld">
                <label>Type <span className="mono t-strong">DELETE ALL EQUIPMENT</span> to confirm <span className="req">*</span></label>
                <input className="input mono" value={confirmation} onChange={(e) => setConfirmation(e.target.value)}
                  placeholder="DELETE ALL EQUIPMENT"
                  autoComplete="off" autoCorrect="off" spellCheck={false}
                  data-testid="danger-confirm-text" />
              </div>
            </>
          ) : (
            <div className="card pad" style={{ background: "#dff6e3", border: "1px solid #b5e0c0" }}>
              <div className="t-strong" style={{ marginBottom: 6 }}>Purge complete · {result.scope}</div>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
                <li>{result.equipment.toLocaleString()} equipment record(s) deleted</li>
                <li>{result.calibration_events.toLocaleString()} calibration event(s) deleted</li>
                <li>{result.certificates.toLocaleString()} certificate(s) deleted</li>
                <li>{result.job_items.toLocaleString()} open job-line(s) deleted</li>
              </ul>
              <div style={{ marginTop: 12 }}><button className="btn gold sm" onClick={closeAll} data-testid="danger-result-done">Done</button></div>
            </div>
          )}
        </Modal>
      )}
    </>
  );
}




// Idempotent customer + contact CSV import. NEVER touches equipment / jobs / certs.
function CustomerCsvImportCard() {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    if (!file) return toast.error("Please attach the customer CSV first.");
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/customers/import-csv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 600000,
      });
      setResult(r.data);
      const created = r.data.customers_created || 0;
      const existed = r.data.customers_existed || 0;
      const contacts = r.data.contacts_created || 0;
      toast.success(
        `${created} new customer(s) created · ${existed} existed · ${contacts} new contact(s) added`
      );
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const closeAll = () => { setOpen(false); setFile(null); setResult(null); };

  return (
    <>
      <div className="card pad" style={{ border: "1px solid #b5d3a9", background: "#f5fbf3" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 36, height: 36, background: "#e1f0d8", borderRadius: 9, display: "flex", alignItems: "center", justifyContent: "center", color: "#36652c" }}>
            <UserPlus size={18} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="t-strong" style={{ fontSize: 14 }}>Import Customers from Caspio CSV (safe / idempotent)</div>
            <div className="s t-mut" style={{ fontSize: 12.5, marginTop: 2 }}>
              Adds missing customers + contacts from a Caspio customer export. Customers that already exist are <em>not</em> overwritten — only new contact rows are added. Sub-customer rows become additional contacts. Equipment, jobs and certificates are never touched. Safe to run multiple times.
            </div>
          </div>
          <button className="btn sm"
            style={{ background: "#36652c", color: "#fff", borderColor: "#36652c" }}
            onClick={() => setOpen(true)}
            data-testid="danger-import-customers">
            <UserPlus size={13} /> Import customers…
          </button>
        </div>
      </div>

      {open && (
        <Modal open onClose={busy ? () => {} : closeAll} icon={Users}
               title="Import Customers from CSV"
               subtitle="Idempotent — existing customers are kept, only new contacts are added."
               footer={<>
                 <button className="btn ghost" onClick={closeAll} disabled={busy}>{result ? "Close" : "Cancel"}</button>
                 {!result && (
                   <button className="btn sm"
                     style={{ background: "#36652c", color: "#fff", borderColor: "#36652c" }}
                     onClick={submit}
                     disabled={busy || !file}
                     data-testid="cust-import-submit">
                     <UserPlus size={13} /> {busy ? "Importing…" : "Import customers"}
                   </button>
                 )}
               </>}>
          {!result ? (
            <>
              <div className="card pad" style={{ background: "#eef7e6", border: "1px solid #b5d3a9", color: "#274d1e", marginBottom: 14, fontSize: 13 }}>
                <strong>What this does:</strong> reads a Caspio Customers export and adds any missing customer rows + contacts. <strong>Already-existing customers are kept exactly as they are</strong> — no fields overwritten, no equipment touched. Re-running the import a second time is a no-op.
              </div>
              <div className="fld" style={{ marginBottom: 14 }}>
                <label>Customer CSV <span className="req">*</span></label>
                <input type="file" accept=".csv,text/csv" className="input"
                       onChange={(e) => setFile(e.target.files?.[0] || null)}
                       data-testid="cust-import-file" />
                {file && (
                  <div className="t-mut" style={{ fontSize: 12, marginTop: 4 }}>
                    {file.name} · {(file.size / 1024).toFixed(0)} KB
                  </div>
                )}
              </div>
              <div className="t-mut" style={{ fontSize: 12.5, lineHeight: 1.6 }}>
                Expected columns (case-sensitive): <span className="mono">Customer_Name</span> (required), <span className="mono">Email</span>, <span className="mono">Customer_Contact_Number</span>, <span className="mono">Contact_Person</span>, <span className="mono">PTE_Online</span>, <span className="mono">Sub_Customer</span> + <span className="mono">Sub_Customer_*</span> for multi-contact rows. Other columns are ignored.
              </div>
            </>
          ) : (
            <div style={{ fontSize: 13.5 }}>
              <div className="card pad" style={{ background: "#eef7e6", border: "1px solid #b5d3a9", color: "#274d1e", marginBottom: 14 }}>
                <strong>Import complete.</strong>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <Stat label="Rows read" value={result.rows_read} />
                <Stat label="Customers created" value={result.customers_created} />
                <Stat label="Customers existed" value={result.customers_existed} />
                <Stat label="Contacts created" value={result.contacts_created} />
                <Stat label="Contacts existed" value={result.contacts_existed} />
                <Stat label="Blank-name skipped" value={result.skipped_blank_name} />
                <Stat label="Errors" value={result.total_errors} highlight={result.total_errors > 0} />
              </div>
              {result.errors && result.errors.length > 0 && (
                <div style={{ marginTop: 14 }}>
                  <div className="t-strong" style={{ fontSize: 13, marginBottom: 6 }}>First {Math.min(result.errors.length, 10)} error(s):</div>
                  <div style={{ background: "#0d0d10", color: "#d9d7d0", fontFamily: "ui-monospace,monospace", fontSize: 12, padding: 10, borderRadius: 8, maxHeight: 220, overflowY: "auto" }}>
                    {result.errors.slice(0, 10).map((e, i) => (
                      <div key={`${e.row}-${i}`}>Row {e.row}: {e.customer_name || "?"} — {e.error}</div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </Modal>
      )}
    </>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div className="card pad" style={{ background: "var(--card-2)", padding: "10px 12px" }}>
      <div className="t-mut" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".05em" }}>{label}</div>
      <div className="t-strong mono" style={{ fontSize: 18, color: highlight ? "var(--red)" : "var(--text)" }}>{(value ?? 0).toLocaleString("en-ZA")}</div>
    </div>
  );
}


function ResetReseedCard() {
  const [open, setOpen] = useState(false);
  const [confirmation, setConfirmation] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [batchId, setBatchId] = useState(null);
  const [progress, setProgress] = useState(null);
  const [wiped, setWiped] = useState(null);

  // Poll batch progress once we have a batch_id
  useEffect(() => {
    if (!batchId) return;
    let stop = false;
    const tick = async () => {
      try {
        const r = await api.get(`/import/batches/${batchId}`);
        setProgress(r.data);
        if (!stop && r.data.status === "running") setTimeout(tick, 2500);
      } catch {
        if (!stop) setTimeout(tick, 4000);
      }
    };
    tick();
    return () => { stop = true; };
  }, [batchId]);

  const submit = async () => {
    if (!file) return toast.error("Please attach the master Caspio CSV first.");
    if (confirmation.trim() !== "RESET AND RESEED EQUIPMENT") {
      return toast.error("Confirmation text doesn't match");
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("confirmation", confirmation.trim());
      const r = await api.post("/equipment/reset-and-reseed", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 600000,
      });
      setWiped(r.data.wiped);
      setBatchId(r.data.batch_id);
      toast.success(
        `Wiped ${r.data.wiped.equipment?.toLocaleString() || 0} equipment row(s). Import running in background.`
      );
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const closeAll = () => {
    setOpen(false); setConfirmation(""); setFile(null);
    setBatchId(null); setProgress(null); setWiped(null);
  };

  const done = progress && (progress.status === "done" || progress.status === "failed");

  return (
    <>
      <div className="card pad" style={{ border: "1px solid #c79c2d", background: "#fffaf0" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 36, height: 36, background: "#fff1c1", borderRadius: 9, display: "flex", alignItems: "center", justifyContent: "center", color: "#8a5a00" }}>
            <RotateCcw size={18} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="t-strong" style={{ fontSize: 14 }}>Reset &amp; Reseed Equipment Register from master CSV</div>
            <div className="s t-mut" style={{ fontSize: 12.5, marginTop: 2 }}>
              One-click: wipes ALL equipment + every certificate, calibration event and job-line, then imports the uploaded Caspio CSV as the new master. Customers, users, settings, price-list and audit log are preserved. Idempotent — duplicates inside the CSV are skipped automatically.
            </div>
          </div>
          <button className="btn sm"
            style={{ background: "#c79c2d", color: "#fff", borderColor: "#c79c2d" }}
            onClick={() => setOpen(true)}
            data-testid="danger-reset-reseed">
            <RotateCcw size={13} /> Reset &amp; Reseed…
          </button>
        </div>
      </div>

      {open && (
        <Modal open onClose={busy ? () => {} : closeAll} icon={RotateCcw}
               title="Reset & Reseed Equipment Register"
               subtitle="Deep wipe + master CSV re-import. Cannot be undone."
               footer={<>
                 <button className="btn ghost" onClick={closeAll} disabled={busy}>{done || progress ? "Close" : "Cancel"}</button>
                 {!batchId && (
                   <button className="btn sm"
                     style={{ background: "#c79c2d", color: "#fff", borderColor: "#c79c2d" }}
                     onClick={submit}
                     disabled={busy || !file || confirmation.trim() !== "RESET AND RESEED EQUIPMENT"}
                     data-testid="danger-reseed-submit">
                     <Sparkles size={13} /> {busy ? "Wiping…" : "Wipe & Import"}
                   </button>
                 )}
               </>}>
          {!batchId ? (
            <>
              <div className="card pad" style={{ background: "#fff7da", border: "1px solid #e3d28a", color: "#5a3f00", marginBottom: 14, fontSize: 13 }}>
                <strong>Heads up:</strong> this will wipe <em>every</em> equipment record, certificate, calibration event and job-line (open <em>and</em> closed). Customers, users and settings are preserved. Re-imports the attached CSV as the new master.
              </div>
              <div className="fld" style={{ marginBottom: 14 }}>
                <label>Master Caspio CSV <span className="req">*</span></label>
                <input type="file" accept=".csv,text/csv" className="input"
                       onChange={(e) => setFile(e.target.files?.[0] || null)}
                       data-testid="danger-reseed-file" />
                {file && (
                  <div className="t-mut" style={{ fontSize: 12, marginTop: 4 }}>
                    {file.name} · {(file.size / 1024 / 1024).toFixed(2)} MB
                  </div>
                )}
              </div>
              <div className="fld">
                <label>Type <span className="mono t-strong">RESET AND RESEED EQUIPMENT</span> to confirm <span className="req">*</span></label>
                <input className="input mono" value={confirmation} onChange={(e) => setConfirmation(e.target.value)}
                       placeholder="RESET AND RESEED EQUIPMENT"
                       autoComplete="off" autoCorrect="off" spellCheck={false}
                       data-testid="danger-reseed-confirm" />
              </div>
            </>
          ) : (
            <div>
              {wiped && (
                <div className="card pad" style={{ background: "#dff6e3", border: "1px solid #b5e0c0", marginBottom: 12 }}>
                  <div className="t-strong" style={{ marginBottom: 6 }}>Wipe complete</div>
                  <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
                    {Object.entries(wiped).map(([k, v]) => (
                      <li key={k}><span className="mono">{k}</span>: {(v || 0).toLocaleString()} deleted</li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="card pad" style={{ background: "#f7f7f4", border: "1px solid var(--line)" }}>
                <div className="t-strong" style={{ marginBottom: 6 }}>
                  Import {progress?.status === "running" ? "in progress…" : progress?.status === "done" ? "complete ✓" : progress?.status === "failed" ? "failed ✗" : "starting…"}
                </div>
                {progress?.counts && (
                  <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
                    <li>Rows seen: {(progress.counts.rows_seen || 0).toLocaleString()}</li>
                    <li>Equipment created: {(progress.counts.equipment_created || 0).toLocaleString()}</li>
                    <li>Equipment enriched: {(progress.counts.equipment_enriched || 0).toLocaleString()}</li>
                    {progress.counts.certificates_created != null && (
                      <li>Certificates created: {(progress.counts.certificates_created || 0).toLocaleString()}</li>
                    )}
                    {progress.counts.calibration_events_created != null && (
                      <li>Calibration events: {(progress.counts.calibration_events_created || 0).toLocaleString()}</li>
                    )}
                    <li>Errors: {(progress.counts.errors || 0).toLocaleString()}</li>
                  </ul>
                )}
                {progress?.error && (
                  <div style={{ color: "var(--red)", fontSize: 12, marginTop: 6 }}>{progress.error}</div>
                )}
              </div>
            </div>
          )}
        </Modal>
      )}
    </>
  );
}
